import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { useAuth } from '../../../../Hooks/useAuth';
import EDRActivityReportToolbar from './EDRActivityReportToolbar';
import { getEdrActivityReportBaseURL } from '../services/edrActivityReportService';
import {
  getExportFileName,
  getExportFileNameWithDate,
} from '../../../Delivery/utils';
import {
  transformEdrActivityResponse,
} from '../utils/edrActivityReportTransformers';
import '../styles/EDRActivityReport.scss';

const baseURL = getEdrActivityReportBaseURL();

const EDRActivityReport = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const [tableInstanceKey, setTableInstanceKey] = useState(0);

  const handleCellClick = useCallback(({ fieldPath, rowData }) => {
    if (fieldPath !== 'projectName') return;

    const projectId = rowData?.projectId;
    if (!projectId) return;

    const projectDetailsUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(projectId))}`;
    window.open(projectDetailsUrl, '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleToolbarReset = (event) => {
      const toolbarButton = event.target.closest('button');
      if (!toolbarButton || !container.contains(toolbarButton)) return;

      const isToolbarReset = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(toolbarButton.querySelector('.pi-refresh'));

      if (!isToolbarReset) return;

      setTableInstanceKey((prev) => prev + 1);
    };

    container.addEventListener('click', handleToolbarReset, true);

    return () => {
      container.removeEventListener('click', handleToolbarReset, true);
    };
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;
    transformEdrActivityResponse(response);
  }, []);

  const handlePaginationChange = useCallback(() => {
    // Pagination disabled for this report
  }, []);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: true },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('EDR Activity Report'),
          sheetName: () => getExportFileNameWithDate('EDR Activity Report'),
          reportTitle: () => 'EDR Activity Report',
          // transformResponse: (payload) => transformEdrActivityExportResponse(payload),
        },
      },
      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse,
      },
      headers: {
        token: userProfile.access_token,
      },
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'EDR Activity Report',
        additionalElements: <EDRActivityReportToolbar/>,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 10,
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 5,
      },
      paginationConfig: {
        page: 1,
        limit: 10,
        total: 0,
        onPaginationChange: handlePaginationChange,
        showPageSizeSelector: false,
      },
      showExpandCollapse: false,
      tableScrollHeight: '70vh',
      maxFrozenColumns: {
        desktop: 1,
        tablet: 1,
        mobile: 1,
      },
    },
  }), [userProfile, handleFilteredApiResponse, handlePaginationChange, handleCellClick]);

  return (
    <div ref={containerRef} className="edr-activity-report-outer-layer">
      <div className="edr-activity-report-card">
        <div className="edr-activity-report-table-utility">
          <TableUtility key={tableInstanceKey} configuration={configuration} />
        </div>
      </div>
    </div>
  );
};

export default EDRActivityReport;
